import type React from 'react';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';

/**
 * UI-tool registry — the host maps a tool NAME to either a React renderer or a
 * fire-once side-effect ("action"), and the library owns everything else:
 * routing the `uiTool` part kind here, parsing/unwrapping the wire args,
 * normalizing the connector-qualified function name, firing actions once per
 * tool-call id (and not on replay), and drawing an optional status chip.
 *
 * The per-entry generic (`render<TArgs>` / `action<TArgs>`) is a COMPILE-TIME
 * assertion only: args arrive off the wire as `unknown` (usually a JSON string,
 * sometimes double-encoded), so a missing field is `undefined` at runtime no
 * matter what the type claims. Pass `opts.parse` when you want a real runtime
 * guard/coercion.
 *
 * Build once at module load and pass the result as `<AiChatProvider uiTools={…} />`:
 *
 *     export const uiTools = createUiToolRegistry()
 *       .render<{ id: string; subAgentId?: string }>('present_dashboard',
 *           (a) => <ScriptDashboardViewer dashboardId={a.id} … />)
 *       .action<{ mode?: 'create' | 'update' }>('present_dashboard_pad',
 *           (a, { env }) => env.updateDashboardPad?.(JSON.parse(a.definitionJson)),
 *           { status: 'Rascunho atualizado' })
 *       .build();
 */

/** Default args shape when a registration omits its generic. */
export type UiToolArgs = Record<string, unknown>;

/** Context handed to every renderer. */
export interface UiToolContext {
    /** Normalized tool name (`UI_` marker + `{Plugin}-`/`{Plugin}_` qualifier stripped). */
    name: string;
    /** This tool call's id — stable across the call's Composing→Executing→Done lifecycle. */
    callId: string;
    /** The tool RESULT, if any. May be an object or a (possibly double-encoded) JSON string. */
    response: unknown;
    /**
     * The tool's CLIENT-ONLY output — the payload it wrote to its SDK `ToolClientOutput` sink,
     * already parsed. This is the preferred render source: it arrives correlated to THIS call (by
     * id) on the result event (so multiple UI calls never race), the LLM never saw it, and it
     * survives reload. `undefined` until the call has responded, or when the tool didn't opt in
     * (fall back to `args`). Replaces the old slot-id-correlated tool-event lookup over `parts`.
     */
    clientOutput: unknown;
    /** Sibling parts of the same interaction (e.g. present_tsx's compiled-JS event rides here). */
    parts: ReadonlyArray<InteractionPart>;
    /** The whole interaction this part belongs to. */
    message: Interaction;
    /** True while the turn is still streaming. */
    onGoing: boolean;
    /** True during historical replay / preview — actions must NOT re-fire side effects. */
    preview: boolean;
}

/** Context handed to actions — adds the host environment. */
export interface UiToolActionContext extends UiToolContext {
    /**
     * The host environment from `EnvironmentProvider` (e.g. `refreshDashboard`,
     * `createDashboardPad`). Empty `{}` when no provider is in scope or during
     * replay (`InertEnvironmentProvider` no-ops every callable) — so action calls
     * simply dead-end rather than throw.
     */
    env: Record<string, unknown>;
}

export type UiToolRenderFn<TArgs> = (args: TArgs, ctx: UiToolContext) => React.ReactNode;
export type UiToolActionFn<TArgs> = (args: TArgs, ctx: UiToolActionContext) => void | Promise<void>;

export interface UiToolRenderOptions<TArgs> {
    /** Runtime arg guard/coercion. Return `null` to render nothing (treated as invalid args). */
    parse?: (raw: unknown) => TArgs | null;
}

export interface UiToolActionOptions<TArgs> {
    /** Runtime arg guard/coercion. Return `null` to skip firing (treated as invalid args). */
    parse?: (raw: unknown) => TArgs | null;
    /**
     * Optional status chip shown AFTER the action fires: a string, or a function
     * of (args, ctx) returning the label (or `null` for no chip). Omit entirely
     * for a silent action — no UI at all, just the side effect.
     */
    status?: string | ((args: TArgs, ctx: UiToolActionContext) => string | null);
}

// --- stored (type-erased) entries -------------------------------------------
// The call-site generic erases to `any` here; safety lives at registration.

export interface UiToolRenderEntry {
    kind: 'render';
    name: string;
    render: UiToolRenderFn<any>;
    parse?: (raw: unknown) => any;
}
export interface UiToolActionEntry {
    kind: 'action';
    name: string;
    run: UiToolActionFn<any>;
    parse?: (raw: unknown) => any;
    status?: string | ((args: any, ctx: UiToolActionContext) => string | null);
}
export type UiToolEntry = UiToolRenderEntry | UiToolActionEntry;

/** A built, read-only registry passed to `<AiChatProvider uiTools={…} />`. */
export interface UiToolRegistry {
    /** Resolve the entry for a RAW (possibly `UI_`-prefixed / connector-qualified) tool name. */
    resolve(rawName: string | undefined | null): UiToolEntry | undefined;
    /** All registered (normalized) names. */
    names(): string[];
}

export interface UiToolRegistryBuilder {
    render<TArgs = UiToolArgs>(name: string, render: UiToolRenderFn<TArgs>, opts?: UiToolRenderOptions<TArgs>): UiToolRegistryBuilder;
    action<TArgs = UiToolArgs>(name: string, run: UiToolActionFn<TArgs>, opts?: UiToolActionOptions<TArgs>): UiToolRegistryBuilder;
    build(): UiToolRegistry;
}

/**
 * Strip the `UI_` render-tool marker wherever it sits — at the very start
 * (`UI_present_dashboard`) or right after a connector's plugin separator
 * (`MyPlugin-UI_present_dashboard` / `MyPlugin_UI_present_dashboard`).
 */
function stripUiMarker(raw: string): string {
    return raw.replace(/(^|[-_])UI_/, '$1');
}

/**
 * Tolerant unwrap for a tool's `clientOutput` payload. It reaches the client as a JSON string
 * (server-serialized) on the live path and as a string off the persisted sibling on replay; peel up
 * to three string layers (only when the trimmed text looks like JSON). Unlike {@link parseUiArgs}
 * this does NOT require an object — a tool may hand the client an array or primitive. Returns the
 * parsed value, the original non-string value as-is, or `undefined` when absent/unparseable.
 */
export function parseClientOutput(raw: unknown): unknown {
    let r: unknown = raw;
    if (r == null) return undefined;
    for (let i = 0; i < 3 && typeof r === 'string'; i++) {
        const t = r.trim();
        if (!(t.startsWith('{') || t.startsWith('[') || t.startsWith('"'))) break;
        try { r = JSON.parse(t); } catch { return undefined; }
    }
    return r;
}

/**
 * Tolerant arg unwrap. Args reach the client as an object on the live path, but as a
 * JSON string — occasionally double-encoded — on the persisted/replay path. Peel up to
 * three string layers (only when the trimmed text looks like JSON), then require an
 * object. Returns `null` when absent/unparseable; mirrors `InteractionPartUtils.uiSlotId`.
 */
export function parseUiArgs(raw: unknown): UiToolArgs | null {
    let r: unknown = raw;
    for (let i = 0; i < 3 && typeof r === 'string'; i++) {
        const t = r.trim();
        if (!(t.startsWith('{') || t.startsWith('['))) break;
        try { r = JSON.parse(t); } catch { return null; }
    }
    return r != null && typeof r === 'object' ? (r as UiToolArgs) : null;
}

export function createUiToolRegistry(): UiToolRegistryBuilder {
    const entries = new Map<string, UiToolEntry>();

    const builder: UiToolRegistryBuilder = {
        render(name, render, opts) {
            const key = stripUiMarker(name);
            entries.set(key, { kind: 'render', name: key, render: render as UiToolRenderFn<any>, parse: opts?.parse });
            return builder;
        },
        action(name, run, opts) {
            const key = stripUiMarker(name);
            entries.set(key, {
                kind: 'action',
                name: key,
                run: run as UiToolActionFn<any>,
                parse: opts?.parse,
                status: opts?.status as UiToolActionEntry['status'],
            });
            return builder;
        },
        build() {
            // Snapshot so post-build mutation of the builder can't leak in.
            const frozen = new Map(entries);
            return {
                resolve(rawName) {
                    if (!rawName) return undefined;
                    const n = stripUiMarker(rawName);
                    const exact = frozen.get(n);
                    if (exact) return exact;
                    // Connector-qualified form ({plugin}-{name} / {plugin}_{name}). Match the
                    // longest registered name that `n` ends with so e.g. `present_chart` wins
                    // over a hypothetical `chart`. (Mirrors the isAskUser suffix matcher.)
                    let best: UiToolEntry | undefined;
                    let bestLen = -1;
                    for (const [name, entry] of frozen) {
                        if ((n.endsWith('-' + name) || n.endsWith('_' + name)) && name.length > bestLen) {
                            best = entry;
                            bestLen = name.length;
                        }
                    }
                    return best;
                },
                names() {
                    return [...frozen.keys()];
                },
            };
        },
    };
    return builder;
}
